// Section 17 and 18 Rations tab view model (24.14): the policies in force once the draft applies,
// the priority order with member counts, saved presets, pins, and an "affects next season"
// preview from project() (24.8). Pure and memoised on (game, draft).
import {
  catalog,
  emptyDecision,
  priorityGroupMembers,
  project,
  type Config,
  type Decision,
  type FoodFloor,
  type GameState,
  type HouseholdId,
  type JobId,
  type PersonId,
  type Policies,
  type PriorityGroupId,
  type RationOverrides,
  type RationPolicyId,
  type SavedPriorityOrder,
  type SeedReserveOption,
  type WarmthOption,
  type WaterRationOption,
} from '../sim/api';

export type RationTabResource = 'food' | 'water' | 'warmth';

/** Section 17's list of priority groups, in the spec's order, plus the catch-all. */
export const PRIORITY_GROUPS: readonly PriorityGroupId[] = [
  'farmers',
  'food-producers',
  'water-carriers',
  'builders',
  'toolmakers',
  'healers',
  'teachers',
  'guards',
  'other-workers',
  'pregnant',
  'children-under-6',
  'children-6-14',
  'elders',
  'sick-or-injured',
  'newcomers',
  'founders',
  'idle-adults',
  'my-household',
  'everyone-else',
];

export const FOOD_POLICIES: readonly RationPolicyId[] = [
  'equal',
  'floor-priority',
  'priority',
  'manual',
];
export const WATER_POLICIES: readonly WaterRationOption[] = [
  'equal',
  'sick-first',
  'workers-first',
  'manual',
];
export const WARMTH_POLICIES: readonly WarmthOption[] = [
  'equal',
  'children-first',
  'elders-first',
  'workers-first',
];
export const SEED_OPTIONS: readonly SeedReserveOption[] = [
  'always-keep',
  'keep-unless-starvation',
  'eat-freely',
];
export const PIN_SHARES = [1, 0.5, 0] as const;

export interface GroupRow {
  id: PriorityGroupId;
  members: number;
}

export interface PinRow {
  id: PersonId;
  name: string;
  ageYears: number;
  job: JobId;
  householdId: HouseholdId;
  /** Pin per resource (food and water per person; warmth is per household). */
  food: number | null;
  water: number | null;
  inMyHousehold: boolean;
}

export interface HouseholdRow {
  id: HouseholdId;
  /** The eldest member's name. */
  name: string;
  members: number;
  food: number | null;
  water: number | null;
  warmth: number | null;
}

export interface RationsVM {
  foodRation: RationPolicyId;
  waterRation: WaterRationOption;
  warmth: WarmthOption;
  foodFloor: FoodFloor;
  floorOptions: FoodFloor[];
  order: GroupRow[];
  /** Groups not in the order, for "add group". */
  unused: GroupRow[];
  savedOrders: SavedPriorityOrder[];
  announce: boolean;
  seedReserve: SeedReserveOption;
  householdSharing: boolean;
  askOnShortage: boolean;
  people: PinRow[];
  households: HouseholdRow[];
  myHousehold: PersonId[];
  /** Fields the draft changes this season. */
  changed: (keyof Policies | 'pins' | 'savedOrders' | 'myHousehold')[];
}

function effective<K extends keyof Policies>(
  game: GameState,
  draft: Decision | null,
  id: K,
): Policies[K] {
  return (draft?.policies[id] as Policies[K] | undefined) ?? game.policies[id];
}

function overrides(game: GameState, draft: Decision | null, r: RationTabResource): RationOverrides {
  return draft?.rations[r] ?? game.rationOverrides[r];
}

/**
 * How many people each listed group holds under this order: a person counts in the first listed
 * group that contains them and "Everyone else" holds only those no other listed group claims
 * (the rationing phase's placement, phases/rationing/groups.ts).
 */
function placedCounts(
  order: readonly PriorityGroupId[],
  members: Record<PriorityGroupId, PersonId[]>,
): GroupRow[] {
  const placed = new Set<PersonId>();
  const counts = new Map<PriorityGroupId, number>();
  for (const id of order) {
    if (id === 'everyone-else') continue;
    const mine = members[id].filter((p) => !placed.has(p));
    for (const p of mine) placed.add(p);
    counts.set(id, mine.length);
  }
  const rest = members['everyone-else'].filter((p) => !placed.has(p)).length;
  return order.map((id) => ({
    id,
    members: id === 'everyone-else' ? rest : (counts.get(id) ?? 0),
  }));
}

let last: { game: GameState; draft: Decision | null; vm: RationsVM } | null = null;

export function rationsVM(game: GameState, draft: Decision | null): RationsVM {
  if (last?.game === game && last.draft === draft) return last.vm;
  const { seasonsPerYear } = catalog();
  const members = priorityGroupMembers(game);
  const order = effective(game, draft, 'foodPriority');
  const pins = {
    food: overrides(game, draft, 'food'),
    water: overrides(game, draft, 'water'),
    warmth: overrides(game, draft, 'warmth'),
  };
  const my = draft?.myHousehold ?? game.myHousehold;
  const people = Object.values(game.people).sort((a, b) => (a.id < b.id ? -1 : 1));
  const households = Object.values(game.households)
    .sort((a, b) => (a.id < b.id ? -1 : 1))
    .map((h) => {
      const eldest = h.memberIds
        .map((id) => game.people[id])
        .filter((p) => p !== undefined)
        .sort((a, b) => b.ageSeasons - a.ageSeasons || (a.id < b.id ? -1 : 1))[0];
      return {
        id: h.id,
        name: eldest?.name ?? h.id,
        members: h.memberIds.length,
        food: pins.food.households[h.id] ?? null,
        water: pins.water.households[h.id] ?? null,
        warmth: pins.warmth.households[h.id] ?? null,
      };
    })
    .filter((h) => h.members > 0);
  const floorOptions = (catalog().policies.find((p) => p.id === 'foodFloor')?.options ?? [
    0.25, 0.5, 0.75,
  ]) as FoodFloor[];
  const changed: RationsVM['changed'] = [];
  if (draft !== null) {
    for (const key of Object.keys(draft.policies) as (keyof Policies)[]) changed.push(key);
    if (Object.keys(draft.rations).length > 0) changed.push('pins');
    if (draft.savedPriorityOrders !== null) changed.push('savedOrders');
    if (draft.myHousehold !== null) changed.push('myHousehold');
  }
  const vm: RationsVM = {
    foodRation: effective(game, draft, 'foodRation'),
    waterRation: effective(game, draft, 'waterRation'),
    warmth: effective(game, draft, 'warmth'),
    foodFloor: effective(game, draft, 'foodFloor'),
    floorOptions,
    order: placedCounts(order, members),
    unused: PRIORITY_GROUPS.filter((id) => !order.includes(id)).map((id) => ({
      id,
      members: members[id].length,
    })),
    savedOrders: draft?.savedPriorityOrders ?? game.savedPriorityOrders,
    announce: effective(game, draft, 'announceRations'),
    seedReserve: effective(game, draft, 'seedReserve'),
    householdSharing: effective(game, draft, 'householdSharing'),
    askOnShortage: effective(game, draft, 'askOnShortage'),
    people: people.map((p) => ({
      id: p.id,
      name: p.name,
      ageYears: Math.floor(p.ageSeasons / seasonsPerYear),
      job: p.job,
      householdId: p.householdId,
      food: pins.food.people[p.id] ?? null,
      water: pins.water.people[p.id] ?? null,
      inMyHousehold: my.includes(p.id),
    })),
    households,
    myHousehold: [...my],
    changed,
  };
  last = { game, draft, vm };
  return vm;
}

/** Move a group one place up (-1) or down (+1); returns the new order. */
export function moveGroup(
  order: readonly PriorityGroupId[],
  id: PriorityGroupId,
  by: -1 | 1,
): PriorityGroupId[] {
  const from = order.indexOf(id);
  const to = from + by;
  if (from < 0 || to < 0 || to >= order.length) return [...order];
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/** Move a group to a new index (drag and drop). */
export function dropGroup(
  order: readonly PriorityGroupId[],
  id: PriorityGroupId,
  toIndex: number,
): PriorityGroupId[] {
  const next = order.filter((g) => g !== id);
  next.splice(Math.max(0, Math.min(next.length, toIndex)), 0, id);
  return next;
}

/** Section 18 "affects next season" preview for rations, from one projected season. */
export interface RationPreview {
  resource: RationTabResource;
  covered: number;
  population: number;
  short: number;
  belowFloor: number;
  /** The shortage dialog would open (Section 17 step 5). */
  willAsk: boolean;
}

let lastPreview: { game: GameState; draft: Decision | null; out: RationPreview[] } | null = null;

export function rationPreview(
  game: GameState,
  draft: Decision | null,
  cfg?: Config,
): RationPreview[] {
  if (lastPreview?.game === game && lastPreview.draft === draft) return lastPreview.out;
  const out: RationPreview[] = [];
  if (game.phaseCursor === null && (game.outcome === null || game.sandbox)) {
    const decision = draft ?? emptyDecision(game);
    const season = project(game, decision, 1, cfg === undefined ? {} : { cfg }).seasons[0];
    if (season !== undefined) {
      const { shortThreshold } = catalog();
      const floor = effective(game, draft, 'foodFloor');
      const ask = effective(game, draft, 'askOnShortage');
      const manual = {
        food: effective(game, draft, 'foodRation') === 'manual',
        water: effective(game, draft, 'waterRation') === 'manual',
        warmth: false,
      };
      for (const resource of ['food', 'water', 'warmth'] as const) {
        if (!season.shortages.includes(resource)) continue;
        const ids = Object.keys(season.needs) as PersonId[];
        const given = season.rations?.[resource] ?? {};
        const got = (id: PersonId): number => given[id] ?? season.needs[id]?.[resource] ?? 1;
        const belowFloor = ids.filter((id) => got(id) < floor - 1e-9).length;
        out.push({
          resource,
          covered: ids.filter((id) => got(id) >= 1 - 1e-9).length,
          population: ids.length,
          short: ids.filter((id) => got(id) < shortThreshold).length,
          belowFloor,
          willAsk: ask && (belowFloor > 0 || manual[resource]),
        });
      }
    }
  }
  lastPreview = { game, draft, out };
  return out;
}
